import React, { PureComponent } from 'react'
import { Card, Empty, Tooltip, message } from 'antd';
import {
    AppstoreOutlined,
    BankOutlined,
    BellOutlined,
    DollarCircleOutlined,
    FileDoneOutlined,
    FileTextOutlined,
    LeftOutlined,
    LockOutlined,
    PhoneOutlined,
    RightOutlined,
    SettingOutlined,
    ShopOutlined,
    StopOutlined,
    TeamOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import Helper from '../../utils/helper';
import { Region } from '../../utils/commonInterfaces';
import { getRegion } from '../regionWrapper';

interface Props extends RouteComponentProps {
    groupActions: any,
    selectedAction: any,
    permission?: any,
}
interface actionAttributes {
    icon?: React.ReactNode,
    name: string,
    description?: string,
    actionName?: string,
}

const disabledActionsMessagesSA: Record<string, string> = {
    createRequest: 'This action is currently disabled: it is mandatory that the user signs the contract and amendment through the app',
    assignUserToFeesModel: 'This action is currently disabled: Free months are not supported.'
}

export const isActionDisabledForSA = (actionName: string) => !!disabledActionsMessagesSA[actionName]

export const getActionIcon = (actionName = '', name = '') => {
    const p = `${actionName} ${name}`.toLowerCase();

    if (p.includes('phone') || p.includes('otp') || p.includes('call') || p.includes('voice')) return <PhoneOutlined />;
    if (p.includes('sms') || p.includes('push') || p.includes('notif')) return <BellOutlined />;
    if (p.includes('user') || p.includes('hr') || p.includes('vip') || p.includes('password') || p.includes('suspend')) return <UserOutlined />;
    if (p.includes('compan') || p.includes('department')) return <BankOutlined />;
    if (p.includes('invoice')) return <FileDoneOutlined />;
    if (p.includes('collect') || p.includes('payment') || p.includes('salary') || p.includes('settlement') || p.includes('fee') || p.includes('deduct') || p.includes('refund')) return <DollarCircleOutlined />;
    if (p.includes('partner') || p.includes('merchant') || p.includes('shop') || p.includes('product') || p.includes('brand') || p.includes('offer')) return <ShopOutlined />;
    if (p.includes('group') || p.includes('team') || p.includes('assign') || p.includes('role')) return <TeamOutlined />;
    if (p.includes('setting') || p.includes('config') || p.includes('global') || p.includes('cap')) return <SettingOutlined />;
    if (p.includes('request') || p.includes('comment') || p.includes('doc')) return <FileTextOutlined />;

    return <AppstoreOutlined />;
}

export class MainLayoutGroups extends PureComponent<Props> {
    region: Region = getRegion() ?? Region.EG;

    handleCardClick = (action: actionAttributes, isDisabled: boolean) => {
        const actionName = action && action.actionName ? action.actionName : ''
        if (isDisabled) {
            const disabledMessage = actionName ? disabledActionsMessagesSA[actionName] : ''
            if (disabledMessage) {
                message.warning({
                    content: disabledMessage,
                    duration: 6,
                })
            }
            return
        }
        if (actionName && this.props && this.props.selectedAction) {
            this.props.selectedAction(action)
        }
    }

    getVisibleActions = () => {
        const { groupActions, permission } = this.props
        const actions = Array.isArray(groupActions) ? groupActions : []

        return actions.filter((action: actionAttributes) => {
            if (!permission || !permission.includes(action.actionName)) return false
            return true
        })
    }

    getSectionTitle = () => {
        const pathname = this.props.location?.pathname || ''
        const segment = pathname.split('/').filter(Boolean)[0] || ''
        return segment ? Helper.unCamelCase(segment) : 'Actions'
    }

    renderCard = (action: actionAttributes, idx: number) => {
        const actionName = action && action.actionName ? action.actionName : ''
        const isDisabled = this.region === Region.SE && actionName ? !!disabledActionsMessagesSA[actionName] : false
        const isRtl = Helper.isArabic(action.name)
        const cardClassName = [
            'card',
            isDisabled ? 'card--disabled' : '',
            isRtl ? 'card--rtl' : '',
        ].filter(Boolean).join(' ')
        const icon = action.icon || getActionIcon(actionName, action.name)
        const disabledMessage = isDisabled ? disabledActionsMessagesSA[actionName] : ''

        const card = (
            <Card
                className={cardClassName}
                bordered={false}
                onClick={() => this.handleCardClick(action, isDisabled)}
                style={{ direction: isRtl ? 'rtl' : 'ltr' }}
            >
                <div className='mainBodyGroups-card'>
                    <span className='mainBodyGroups-card__icon'>{isDisabled ? <StopOutlined /> : icon}</span>
                    <div className='mainBodyGroups-card__content'>
                        <div className='mainBodyGroups-card__title'>{action.name}</div>
                        {action.description && (
                            <div className='mainBodyGroups-card__desc'>{action.description}</div>
                        )}
                    </div>
                    <span className='mainBodyGroups-card__arrow'>
                        {isRtl ? <LeftOutlined /> : <RightOutlined />}
                    </span>
                </div>
            </Card>
        )

        const item = disabledMessage ? (
            <Tooltip
                overlayClassName='mainBodyGroups-tooltip'
                placement={isRtl ? 'topRight' : 'top'}
                mouseEnterDelay={0.12}
                color='#fff'
                title={
                    <div className='mainBodyGroups-tooltip__body'>
                        <span className='mainBodyGroups-tooltip__icon'>
                            <StopOutlined />
                        </span>
                        <div className='mainBodyGroups-tooltip__copy'>
                            <div className='mainBodyGroups-tooltip__title'>Action unavailable</div>
                            <div className='mainBodyGroups-tooltip__text'>
                                {disabledMessage.replace(/^This action is currently disabled:\s*/i, '')}
                            </div>
                        </div>
                    </div>
                }
            >
                <div className='mainBodyGroups-tooltipTarget'>{card}</div>
            </Tooltip>
        ) : card

        return <div key={actionName || idx}>{item}</div>
    }

    renderEmpty = (hasPermission: boolean) => {
        if (!hasPermission) {
            return (
                <Empty
                    className='mainBodyGroups-empty'
                    image={<LockOutlined className='mainBodyGroups-empty__icon' />}
                    description={
                        <div>
                            <div className='mainBodyGroups-empty__title'>Access restricted</div>
                            <div className='mainBodyGroups-empty__text'>You don't have permission to view this portal</div>
                        </div>
                    }
                />
            )
        }

        return (
            <Empty
                className='mainBodyGroups-empty'
                description={
                    <div>
                        <div className='mainBodyGroups-empty__title'>No actions available</div>
                        <div className='mainBodyGroups-empty__text'>
                            There are no actions assigned to this section
                        </div>
                    </div>
                }
            />
        )
    }

    render() {
        const { permission } = this.props
        const visibleActions = this.getVisibleActions()
        const hasPermission = Boolean(permission)

        return (
            <div className='mainBodyGroups'>
                {hasPermission && (
                    <div className='mainBodyGroups-header'>
                        <div>
                            <h1 className='mainBodyGroups-header__title'>{this.getSectionTitle()}</h1>
                            <p className='mainBodyGroups-header__subtitle'>
                                {`${visibleActions.length} ${visibleActions.length === 1 ? 'action' : 'actions'} available`}
                            </p>
                        </div>
                    </div>
                )}

                {hasPermission && visibleActions.length > 0 ? (
                    <div className='mainBodyGroups-grid'>
                        {visibleActions.map((action: actionAttributes, idx: number) => this.renderCard(action, idx))}
                    </div>
                ) : this.renderEmpty(hasPermission)}
            </div>
        )
    }
}

export default withRouter(MainLayoutGroups)
